// ─────────────────────────────────────────────────────────────────────────────
// BucklyPage — the TRADING domain's performance-psychology agent. Her page body
// from Claude Design (Buckly.dc.html), exactly as designed, in her existing slot
// (owner rulings R21, R37, R38, R40; build plan v5, round B-1).
//
// FOLLOWS THE MackPage PATTERN: this file owns only what every tab shares —
// the header, the status line, the tab row, the page state and the scroll frame.
// Each tab is its own file under buckly/; their values are the design's
// placeholders (buckly/placeholders.ts, generated) until her memory is wired, tab
// by tab, in B-4.
//
// NOT ADDED (R38): the design's own Trading nav, market-session ticker and clock
// (the desktop's Trading area has its own). With the nav gone, the body's 92px
// top padding (64px nav + 28px) is 28px, and the 52px the ticker reserved at the
// bottom is not reserved. The design's full-bleed fixed background is pinned to
// her slot instead of the window, so nothing outside her page changes.
//
// One backend read (B-4.1b): her Today API, for the header's status line and the
// Today tab (buckly/todayApi.ts), with the authToken AgentViewScreen mounts it
// with; read again when a session ends (B-4.2: the header counts ended sessions).
// Her Session tab reads her Session API itself (buckly/sessionApi.ts). The orb is untouched: it already calls the open agent, and this page is
// reached as {TRADING, BUCKLY} (B-0; R39).
// ─────────────────────────────────────────────────────────────────────────────

import React, { useState } from 'react';
import './buckly/buckly.css';
import bucklyBg from './buckly/assets/buckly-bg.png';
import { ASSET, BucklyState, Hover, SetBuckly, sx } from './buckly/primitives';
import { BUCKLY_BG_FILTER, DESIGN_GROUND, ORB_CLEARANCE, PAGE_BG, PAGE_TEXT, SANS } from './buckly/tokens';
import { DESIGN_STATE } from './buckly/placeholders';
import { TodayTab } from './buckly/TodayTab';
import { panel, useBucklyToday } from './buckly/todayApi';
import { SessionTab } from './buckly/SessionTab';
import { PatternsTab } from './buckly/PatternsTab';
import { ReviewTab } from './buckly/ReviewTab';
import { CrossReadsTab } from './buckly/CrossReadsTab';
import { TherapistTab } from './buckly/TherapistTab';
import { DreamsTab } from './buckly/DreamsTab';
import { ReferenceTab } from './buckly/ReferenceTab';

export interface BucklyPageProps {
  authToken?: string | null;
  onBack?: () => void;
}

export default function BucklyPage(props: BucklyPageProps) {
  const [s, setS] = useState<BucklyState>(DESIGN_STATE);
  const set: SetBuckly = (patch) =>
    setS((prev) => ({ ...prev, ...(typeof patch === 'function' ? patch(prev) : patch) }));
  const tab = s.tab;
  const go = (t: string) => () => set({ tab: t });
  // Her Today (B-4.1b), read when the page opens and each time the Today tab is shown. The header's status
  // line is her contract's §9: "no sessions yet" (no count, no confidence: R96, D38); drawn until it is current.
  const [ended, setEnded] = useState(0);
  const today = useBucklyToday(props.authToken, `${tab === 'today'}:${ended}`);
  const hdr = panel(today, 'header');
  // The design's page-level values (Buckly.dc.html:1253-1284).
  const v = {
    isToday: tab === 'today', notToday: tab !== 'today',
    isSession: tab === 'session', notSession: tab !== 'session',
    isPatterns: tab === 'patterns', notPatterns: tab !== 'patterns',
    isReview: tab === 'review', notReview: tab !== 'review',
    isCrossReads: tab === 'cross', notCrossReads: tab !== 'cross',
    isTherapist: tab === 'therapist', notTherapist: tab !== 'therapist',
    isDreams: tab === 'dreams', notDreams: tab !== 'dreams',
    isReference: tab === 'reference', notReference: tab !== 'reference',
    onToday: go('today'), onSession: go('session'), onPatterns: go('patterns'), onReview: go('review'),
    onCrossReads: go('cross'), onTherapist: go('therapist'), onDreams: go('dreams'), onReference: go('reference'),
    onReset: go('today'),
    hdrDrawn: !hdr, hdrLive: !!hdr, hdrLine: hdr ? hdr.status_line : '',
  };

  return (
    <div data-buckly-page="" style={{
      // Her OWN scroll frame, as MackPage's: height:100% (never 100vh) so the
      // agent view's tab bar is respected. AppShell is not touched.
      height: '100%', minHeight: 0, minWidth: 0,
      display: 'flex', flexDirection: 'column', overflow: 'hidden',
      position: 'relative', background: PAGE_BG, color: PAGE_TEXT, fontFamily: SANS,
      ...DESIGN_GROUND,
    }}>
      {/* the design's starfield + hexagon rings (Buckly.dc.html:25), pinned to her slot,
          and dulled on this layer only (tokens.ts BUCKLY_BG_DIM; owner, B-1b) */}
      <div style={{ ...sx("position:fixed; inset:0; z-index:0; pointer-events:none; background-color:#04060e; background-size:cover; background-position:center center; background-repeat:no-repeat;"), position: 'absolute', backgroundImage: `url(${bucklyBg})`, filter: BUCKLY_BG_FILTER }} />
      <div style={{ position: 'relative', zIndex: 1, flex: 1, minHeight: 0, overflowY: 'auto', overflowX: 'hidden', overscrollBehavior: 'contain' }}>
        <div style={{ ...sx("position:relative; z-index:1; max-width:1640px; margin:0 auto; padding:92px 40px 24px;"), paddingTop: 28 }}>
          {/* HEADER — clear of the desktop's corner orb (tokens.ts ORB_CLEARANCE) */}
          <div style={{ paddingRight: ORB_CLEARANCE }}>
          {/* ⟨design Buckly.dc.html:47-64 (bound copy: b4/s2/ui/make_bound.py) — generated by dc2tsx.py; regenerate, never hand-edit⟩ */}
          <div style={sx("display:flex; align-items:flex-start; justify-content:space-between; gap:20px; flex-wrap:wrap;")}>
            <div style={sx("display:flex; align-items:center; gap:18px;")}>
              <div style={sx("flex:none; width:56px; height:56px; border-radius:50%; border:1.5px solid rgba(95,184,240,0.45); display:flex; align-items:center; justify-content:center; box-shadow:inset 0 0 18px rgba(60,150,230,0.14), 0 0 16px rgba(60,150,230,0.1);")}>
                <img src={ASSET["buckly-mark.png"]} alt="Buckly" style={sx("width:46px; height:46px; object-fit:contain; mix-blend-mode:screen; display:block;")} />
              </div>
              <h1 style={sx("margin:0; font-size:25px; font-weight:600; letter-spacing:-0.01em; color:#f3f7f8;")}>Buckly — Psychology</h1>
            </div>
            <div style={sx("display:flex; align-items:center; gap:16px; flex-wrap:wrap;")}>
              <div style={sx("display:inline-flex; align-items:center; gap:9px; padding:8px 14px; border-radius:10px; background:rgba(255,255,255,0.02); border:1px solid rgba(255,255,255,0.09); font-family:'JetBrains Mono',monospace; font-size:12px; color:rgba(220,228,230,0.72);")}>
                <span style={sx("width:7px; height:7px; border-radius:50%; background:#5fb8f0; box-shadow:0 0 8px rgba(95,184,240,0.8); animation:bucklyPulse 2.4s ease-in-out infinite;")} />
                {v.hdrDrawn && (
                  <>
                    compounding · 142 sessions · high confidence
                  </>
                )}
                {v.hdrLive && (
                  <>
                    {v.hdrLine}
                  </>
                )}
              </div>
              <Hover as="button" onClick={v.onReset} style={sx("display:inline-flex; align-items:center; gap:7px; padding:8px 15px; border-radius:9px; background:rgba(255,255,255,0.03); border:1px solid rgba(255,255,255,0.12); color:rgba(226,232,234,0.72); font-size:13px; cursor:pointer; font-family:inherit; transition:border-color .25s, color .25s;")} hover={sx("border-color:rgba(95,184,240,0.45); color:#8fcef5;")}>
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                  <path d="M3 12a9 9 0 1 0 3-6.7L3 8" />
                  <path d="M3 3v5h5" />
                </svg>
                Reset
              </Hover>
            </div>
          </div>
          {/* ⟨/design⟩ */}
          </div>

          {/* TABS */}
          {/* ⟨design Buckly.dc.html:67-100 — generated by dc2tsx.py; regenerate, never hand-edit⟩ */}
          <div style={sx("margin-top:22px; border-bottom:1px solid rgba(255,255,255,0.08); display:flex; gap:6px; flex-wrap:wrap;")}>
            <button onClick={v.onToday} style={sx("position:relative; padding:11px 16px; background:none; border:none; margin-bottom:-1px; font-family:inherit; font-size:14px; cursor:pointer; color:rgba(220,228,230,0.5);")}>
              {v.isToday && (
                <>
                  <span style={sx("color:#f3f7f8; font-weight:500;")}>Today</span>
                  <span style={sx("position:absolute; left:14px; right:14px; bottom:-1px; height:2px; border-radius:2px; background:#3c96e6;")} />
                </>
              )}
              {' '}
              {v.notToday && (
                <span>Today</span>
              )}
            </button>
            <button onClick={v.onSession} style={sx("position:relative; padding:11px 16px; background:none; border:none; margin-bottom:-1px; font-family:inherit; font-size:14px; cursor:pointer; color:rgba(220,228,230,0.5);")}>
              {v.isSession && (
                <>
                  <span style={sx("color:#f3f7f8; font-weight:500;")}>Session</span>
                  <span style={sx("position:absolute; left:14px; right:14px; bottom:-1px; height:2px; border-radius:2px; background:#3c96e6;")} />
                </>
              )}
              {' '}
              {v.notSession && (
                <span>Session</span>
              )}
            </button>
            <button onClick={v.onPatterns} style={sx("position:relative; padding:11px 16px; background:none; border:none; margin-bottom:-1px; font-family:inherit; font-size:14px; cursor:pointer; color:rgba(220,228,230,0.5);")}>
              {v.isPatterns && (
                <>
                  <span style={sx("color:#f3f7f8; font-weight:500;")}>Patterns</span>
                  <span style={sx("position:absolute; left:14px; right:14px; bottom:-1px; height:2px; border-radius:2px; background:#3c96e6;")} />
                </>
              )}
              {' '}
              {v.notPatterns && (
                <span>Patterns</span>
              )}
            </button>
            <button onClick={v.onReview} style={sx("position:relative; padding:11px 16px; background:none; border:none; margin-bottom:-1px; font-family:inherit; font-size:14px; cursor:pointer; color:rgba(220,228,230,0.5);")}>
              {v.isReview && (
                <>
                  <span style={sx("color:#f3f7f8; font-weight:500;")}>Review</span>
                  <span style={sx("position:absolute; left:14px; right:14px; bottom:-1px; height:2px; border-radius:2px; background:#3c96e6;")} />
                </>
              )}
              {' '}
              {v.notReview && (
                <span>Review</span>
              )}
            </button>
            <button onClick={v.onCrossReads} style={sx("position:relative; padding:11px 16px; background:none; border:none; margin-bottom:-1px; font-family:inherit; font-size:14px; cursor:pointer; color:rgba(220,228,230,0.5);")}>
              {v.isCrossReads && (
                <>
                  <span style={sx("color:#f3f7f8; font-weight:500;")}>Cross-Reads</span>
                  <span style={sx("position:absolute; left:14px; right:14px; bottom:-1px; height:2px; border-radius:2px; background:#3c96e6;")} />
                </>
              )}
              {' '}
              {v.notCrossReads && (
                <span>Cross-Reads</span>
              )}
            </button>
            <button onClick={v.onTherapist} style={sx("position:relative; padding:11px 16px; background:none; border:none; margin-bottom:-1px; font-family:inherit; font-size:14px; cursor:pointer; color:rgba(220,228,230,0.5);")}>
              {v.isTherapist && (
                <>
                  <span style={sx("color:#f3f7f8; font-weight:500;")}>Therapist Area</span>
                  <span style={sx("position:absolute; left:14px; right:14px; bottom:-1px; height:2px; border-radius:2px; background:#3c96e6;")} />
                </>
              )}
              {' '}
              {v.notTherapist && (
                <span>Therapist Area</span>
              )}
            </button>
            <button onClick={v.onDreams} style={sx("position:relative; padding:11px 16px; background:none; border:none; margin-bottom:-1px; font-family:inherit; font-size:14px; cursor:pointer; color:rgba(220,228,230,0.5);")}>
              {v.isDreams && (
                <>
                  <span style={sx("color:#f3f7f8; font-weight:500;")}>Dreams</span>
                  <span style={sx("position:absolute; left:14px; right:14px; bottom:-1px; height:2px; border-radius:2px; background:#3c96e6;")} />
                </>
              )}
              {' '}
              {v.notDreams && (
                <span>Dreams</span>
              )}
            </button>
            <button onClick={v.onReference} style={sx("position:relative; padding:11px 16px; background:none; border:none; margin-bottom:-1px; font-family:inherit; font-size:14px; cursor:pointer; color:rgba(220,228,230,0.5);")}>
              {v.isReference && (
                <>
                  <span style={sx("color:#f3f7f8; font-weight:500;")}>Reference</span>
                  <span style={sx("position:absolute; left:14px; right:14px; bottom:-1px; height:2px; border-radius:2px; background:#3c96e6;")} />
                </>
              )}
              {' '}
              {v.notReference && (
                <span>Reference</span>
              )}
            </button>
          </div>
          {/* ⟨/design⟩ */}

          {v.isToday && <TodayTab today={today} />}
          {v.isSession && <SessionTab s={s} set={set} authToken={props.authToken} onEnded={() => setEnded((n) => n + 1)} />}
          {v.isPatterns && <PatternsTab s={s} set={set} />}
          {v.isReview && <ReviewTab s={s} set={set} />}
          {v.isCrossReads && <CrossReadsTab />}
          {v.isTherapist && <TherapistTab />}
          {v.isDreams && <DreamsTab s={s} set={set} />}
          {v.isReference && <ReferenceTab />}
        </div>
      </div>
    </div>
  );
}
